import type { ServerSummary } from "@woven/contracts";
import { useState } from "react";
import { EditServerCapacityModal } from "../../features/servers/EditServerCapacityModal";
import { ServerCapacityMetric } from "../../features/servers/ServerCapacityMetric";
import { canEditServerCapacity } from "../../features/servers/serverCapacity";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useParams } from "react-router";
import { useAuth } from "../../auth/context";
import { ServerActions } from "../../features/servers/ServerActions";
import { ServerMonitoring } from "../../features/servers/ServerMonitoring";
import { SpatialSubspaces } from "../../features/servers/SpatialSubspaces";
import { EmptyState, ErrorState, LoadingState } from "../../components/StatusView";
import { useServers, type ServersState } from "../../features/servers/serverHooks";

export function ConsoleServerDetailPage() {
  const { user } = useAuth();
  const location = useLocation();
  const { serverId } = useParams();
  return (
    <div className="console-page">
      <ServerDetail key={`${user?.uid ?? "guest"}:${serverId}:${location.key}`} owner={!!user} />
    </div>
  );
}

function ServerDetail({ owner }: { owner: boolean }) {
  const { state, reload } = useServers();
  const [editing, setEditing] = useState<ServerSummary | null>(null);
  const updated =
    state.status === "ready" ? state.data.items.find((item) => item.id === editing?.id) : undefined;
  return (
    <>
      <ServerDetailContent owner={owner} state={state} reload={reload} onEdit={setEditing} />
      {owner && editing && (
        <EditServerCapacityModal
          server={updated ?? editing}
          ready={!!updated}
          onClose={() => setEditing(null)}
          onRefresh={reload}
        />
      )}
    </>
  );
}

function ServerDetailContent({
  owner,
  state,
  reload,
  onEdit,
}: {
  owner: boolean;
  state: ServersState;
  reload: () => void;
  onEdit: (server: ServerSummary) => void;
}) {
  const params = useParams();
  const serverId = params.serverId;

  if (state.status === "loading") {
    return (
      <>
        <BackToServers />
        <LoadingState label="Loading server…" />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <BackToServers />
        <ErrorState message={state.message} onRetry={reload} />
      </>
    );
  }

  const server = state.data.items.find((item) => item.id === serverId);
  if (!server) {
    return (
      <EmptyState title="Server not found" body={`No server matches id "${serverId ?? ""}".`}>
        <Link to="/console/servers" className="btn btn--primary">
          Back to Servers
        </Link>
      </EmptyState>
    );
  }

  const productDetails = (
    <dl className="detail-list">
      <div className="detail-row">
        <dt>Product ID</dt>
        <dd className="cell-mono">{server.id}</dd>
      </div>
      <div className="detail-row">
        <dt>Product Name</dt>
        <dd>{server.name}</dd>
      </div>
      <div className="detail-row">
        <dt>Resource</dt>
        <dd>{server.resourceId === "global" ? "Global Fabric" : "Unassigned"}</dd>
      </div>
      <div className="detail-row">
        <dt>Capacity</dt>
        <dd>
          <ServerCapacityMetric server={server} />
          {owner && (
            <button
              id="edit-server-capacity"
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!canEditServerCapacity(server)}
              onClick={() => onEdit(server)}
            >
              Edit Capacity
            </button>
          )}
        </dd>
      </div>
      <div className="detail-row">
        <dt>Status</dt>
        <dd className="cell-mono">{server.status}</dd>
      </div>
      <div className="detail-row">
        <dt>Region</dt>
        <dd className="cell-mono">{server.region}</dd>
      </div>
      <div className="detail-row">
        <dt>Created</dt>
        <dd className="cell-mono">{new Date(server.createdAt).toLocaleString()}</dd>
      </div>
    </dl>
  );

  return (
    <>
      <div className="server-detail-heading">
        <BackToServers />
        <div className="console-page-head">
          <h1 className="console-page-title">{server.name}</h1>
          <p className="console-page-sub">
            <span className="cell-mono">{server.id}</span>
          </p>
        </div>
      </div>

      {owner ? (
        <ServerActions server={server} reload={reload}>
          {productDetails}
        </ServerActions>
      ) : (
        <section className="panel server-detail-summary" aria-label="Server details">
          {productDetails}
          <Link
            className="btn btn--ghost"
            to={`/console/logs?serverId=${encodeURIComponent(server.id)}`}
          >
            View Logs
          </Link>
        </section>
      )}

      {owner && <SpatialSubspaces server={server} />}
      <ServerMonitoring server={server} />
    </>
  );
}

function BackToServers() {
  return (
    <Link
      to="/console/servers"
      className="preview-icon-btn server-detail-back"
      aria-label="Back to Servers"
      title="Back to Servers"
    >
      <ArrowLeft size={17} strokeWidth={1.7} absoluteStrokeWidth aria-hidden="true" />
    </Link>
  );
}
